/**
 * Text input for chat conversations with file attachment support.
 * Supports clipboard paste, paperclip file picker, and drag-and-drop.
 * Reusable across agent, assistant, and avatar chats.
 */
import { useState, KeyboardEvent, useRef, useImperativeHandle, forwardRef, useEffect, useCallback } from 'react';
import { Send, Square, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { useClipboardPaste } from '@/shared/files/hooks/useClipboardPaste';
import { PendingFilesPreview } from '@/shared/files/components/PendingFilesPreview';
import { useUploadFile } from '@/shared/files/hooks/useFiles';

/** A clickable suggestion shown inside the input when empty */
export interface ChatSuggestion {
  /** Display text */
  label: string;
  /** Prompt to send when clicked */
  prompt: string;
}

interface ChatInputProps {
  /** Called when user sends a message with optional file IDs */
  onSend: (text: string, fileIds?: string[]) => void;
  /** Called when user clicks stop button to interrupt generation */
  onStop?: () => void;
  /** Disable input (e.g., while connecting or sending) */
  disabled?: boolean;
  /** Whether AI is currently generating a response */
  isGenerating?: boolean;
  /** Placeholder text override */
  placeholder?: string;
  /** Whether to auto-focus on mount */
  autoFocus?: boolean;
  /** When this value changes (e.g., chat session switched), refocus the input.
   *  Only takes effect when `autoFocus` is true. Pass the current session/chat
   *  id so loading a historic chat or creating a new one puts the cursor in
   *  the textarea without the user having to click. */
  focusSignal?: string | number | null;
  /** Initial value to populate the input with */
  initialValue?: string;
  /** Callback when initial value has been consumed (set to empty string to clear) */
  onInitialValueConsumed?: () => void;
  /** Accepted MIME types for file attachments (from provider capabilities). Empty = no file support. */
  acceptedMimeTypes?: string[];
  /** Content rendered on the left side of the toolbar row (e.g., tool modules indicator) */
  toolbarLeft?: React.ReactNode;
  /** Content rendered on the right side of the toolbar row (e.g., model selector) */
  toolbarRight?: React.ReactNode;
  /** Content that replaces the entire input area when present (e.g., pending interactive block) */
  replacementContent?: React.ReactNode;
  /** Clickable suggestions shown inside the input when empty (max 2 shown) */
  suggestions?: ChatSuggestion[];
}

export interface ChatInputHandle {
  focus: () => void;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({
  onSend,
  onStop,
  disabled = false,
  isGenerating = false,
  placeholder,
  autoFocus = false,
  focusSignal,
  initialValue,
  onInitialValueConsumed,
  acceptedMimeTypes = [],
  toolbarLeft,
  toolbarRight,
  replacementContent,
  suggestions,
}, ref) => {
  const { t } = useTranslation(['common', 'aiChat']);
  const [textInput, setTextInput] = useState(initialValue || '');
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = useUploadFile();

  const supportsFiles = acceptedMimeTypes.length > 0;

  // Build accept string for file input (e.g., "image/jpeg,image/png,application/pdf")
  const acceptString = acceptedMimeTypes.join(',');

  const handleReject = useCallback((file: File) => {
    const dotIndex = file.name.lastIndexOf('.');
    const extension = dotIndex > 0 ? file.name.slice(dotIndex + 1).toUpperCase() : t('aiChat:fileTypeFallback');
    toast.error(t('aiChat:unsupportedFileType', { extension }));
  }, [t]);

  const {
    pendingFiles,
    handlePaste,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleFileInputChange,
    removePendingFile,
    clearPendingFiles,
    isDragging,
  } = useClipboardPaste({
    acceptedTypes: acceptedMimeTypes,
    onReject: handleReject,
  });

  // Apply initial value when it changes (e.g., from sessionStorage)
  useEffect(() => {
    if (initialValue) {
      setTextInput(initialValue);
      onInitialValueConsumed?.();
    }
  }, [initialValue, onInitialValueConsumed]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const handleSend = useCallback(async () => {
    const hasText = textInput.trim().length > 0;
    const hasFiles = pendingFiles.length > 0;
    if (!hasText && !hasFiles) return;

    if (hasFiles) {
      // Upload files first, then send message with file IDs
      setIsUploading(true);
      try {
        const fileIds: string[] = [];
        for (const file of pendingFiles) {
          const result = await uploadFile.mutateAsync({
            file,
            is_user_file: false,
          });
          fileIds.push(result.data.id);
        }
        onSend(textInput.trim(), fileIds);
        setTextInput('');
        clearPendingFiles();
      } catch {
        // The global mutation-error handler shows the specific translated toast.
      } finally {
        setIsUploading(false);
      }
    } else {
      onSend(textInput.trim());
      setTextInput('');
    }
  }, [textInput, pendingFiles, onSend, uploadFile, clearPendingFiles]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaperclipClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasContent = textInput.trim().length > 0 || pendingFiles.length > 0;
  const isBusy = isUploading || isGenerating;
  const isDisabled = disabled || isBusy;
  const showSuggestions = suggestions && suggestions.length > 0 && !hasContent && !isBusy && !disabled;

  // Re-focus when input transitions from disabled to enabled (e.g., after agent greeting).
  // Uses setTimeout to fire after any pending focus restoration (e.g., Radix dropdown onCloseAutoFocus).
  const wasDisabledRef = useRef(isDisabled);
  useEffect(() => {
    const wasDisabled = wasDisabledRef.current;
    wasDisabledRef.current = isDisabled;
    if (wasDisabled && !isDisabled && autoFocus) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isDisabled, autoFocus]);

  // Re-focus when the caller signals a new session (new chat created, historic
  // chat loaded). Skips when the input is disabled (streaming) so we don't
  // fight the user's current interaction.
  useEffect(() => {
    if (!autoFocus) return;
    if (isDisabled) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal]);

  const handleSuggestionClick = useCallback((prompt: string) => {
    setTextInput(prompt);
    inputRef.current?.focus();
  }, []);

  const hasToolbar = !!(toolbarLeft || toolbarRight || supportsFiles);

  return (
    <div
      className="relative shrink-0"
      onDragEnter={supportsFiles ? handleDragEnter : undefined}
      onDragLeave={supportsFiles ? handleDragLeave : undefined}
      onDragOver={supportsFiles ? handleDragOver : undefined}
      onDrop={supportsFiles ? handleDrop : undefined}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5">
          <p className="text-sm font-medium text-primary">{t('aiChat:dropFilesHere')}</p>
        </div>
      )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="mb-2">
          <PendingFilesPreview
            files={pendingFiles}
            onRemove={removePendingFile}
            compact
          />
        </div>
      )}

      {/* Replacement content (e.g., pending question) replaces the textarea */}
      {replacementContent ? (
        <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden flex flex-col">
          <div className="overflow-y-auto max-h-[40vh]">
            {replacementContent}
          </div>

          {/* Toolbar row — always visible even with replacement content */}
          {hasToolbar && (
            <div className="flex items-center gap-1 px-2 py-1.5 border-t border-border/40 shrink-0">
              <div className="flex items-center gap-1 min-w-0 flex-1">
                {toolbarLeft}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {toolbarRight}
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        {/* Textarea area */}
        <Textarea
          ref={inputRef}
          placeholder={placeholder || t('avatar.typeMessage')}
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={supportsFiles ? handlePaste : undefined}
          disabled={isDisabled}
          autoFocus={autoFocus}
          className="min-h-[44px] resize-none border-0 shadow-none focus-visible:ring-0 rounded-none px-3 py-2.5"
          rows={1}
        />

        {/* Inline suggestions — subtle clickable prompts when input is empty */}
        {showSuggestions && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 pb-1.5 max-h-[2.75rem] overflow-hidden">
            {suggestions.slice(0, 4).map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSuggestionClick(s.prompt)}
                className="text-xs text-muted-foreground/60 hover:text-foreground transition-colors truncate max-w-[45%] cursor-pointer"
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Toolbar row */}
        {hasToolbar && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-t border-border/40">
            {/* Left side: paperclip + toolbarLeft content */}
            <div className="flex items-center gap-1 min-w-0 flex-1">
              {supportsFiles && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground"
                      onClick={handlePaperclipClick}
                      disabled={isDisabled}
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('aiChat:attachFile')}</TooltipContent>
                </Tooltip>
              )}
              {toolbarLeft}
            </div>

            {/* Right side: toolbarRight + send/stop */}
            <div className="flex items-center gap-1 shrink-0">
              {toolbarRight}
              {isGenerating ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onStop}
                      className="h-8 w-8 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Square className="h-4 w-4 fill-current" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('aiChat:stopGenerating')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!hasContent || disabled || isUploading}
                  className={cn(
                    'h-8 w-8 rounded-full transition-colors',
                    hasContent
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Fallback: send button when no toolbar */}
        {!hasToolbar && (
          <div className="flex justify-end px-2 py-1.5 border-t border-border/40">
            {isGenerating ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onStop}
                    className="h-8 w-8 rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Square className="h-4 w-4 fill-current" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('aiChat:stopGenerating')}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!hasContent || disabled || isUploading}
                className={cn(
                  'h-8 w-8 rounded-full transition-colors',
                  hasContent
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Hidden file input */}
      {supportsFiles && (
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept={acceptString}
          onChange={handleFileInputChange}
        />
      )}
    </div>
  );
});

ChatInput.displayName = 'ChatInput';
